/**
 * Q-02 動態檢舉佇列 CMS helpers (contract §4「v3.4 補充」). Pure — no React / Firebase — so node:test can import them.
 */

export type ArtistModerationType = "content" | "comment";
export type ArtistModerationStatus = "open" | "resolved";
export type ArtistReportReason = "spam" | "harassment" | "sexual" | "violence" | "other";
export type ArtistModerationDecision = "dismiss" | "hide_content" | "hide_comment" | "block_author" | "suspend_creator";

export const REPORT_REASON_LABELS: Record<ArtistReportReason, string> = {
  spam: "垃圾訊息或詐騙",
  harassment: "騷擾或仇恨",
  sexual: "色情或性暗示",
  violence: "暴力或危險行為",
  other: "其他",
};
export const DECISION_LABELS: Record<ArtistModerationDecision, string> = {
  dismiss: "不處理（未違規）",
  hide_content: "隱藏動態",
  hide_comment: "隱藏留言",
  block_author: "封鎖留言者（全平台）",
  suspend_creator: "停權創作者",
};
/** What each decision does, shown under the buttons so ops know the blast radius before confirming. */
export const DECISION_HELP: Record<ArtistModerationDecision, string> = {
  dismiss: "結案，內容保持原狀。",
  hide_content: "動態從所有列表、專頁與分享連結消失，只有營運能重新上架。",
  hide_comment: "留言對所有人隱藏，藝人團隊無法自行恢復。",
  block_author: "隱藏這則留言，並封鎖該帳號（無法再留言、無法使用星光功能）；可在評論管理解除封鎖。",
  suspend_creator: "停用此創作者帳號的所有工作區權限（無法發文、回覆、管理）。",
};
export const CONTENT_TYPE_LABELS: Record<string, string> = { post: "貼文", vlog: "Vlog", story: "限動" };
export const SLA_MS = 24 * 60 * 60 * 1000;
export const RESOLVE_NOTE_MAX = 500;

export const reasonLabel = (v: string | null | undefined): string =>
  v && v in REPORT_REASON_LABELS ? REPORT_REASON_LABELS[v as ArtistReportReason] : "其他";
export const typeLabel = (type: ArtistModerationType, contentType: string | null | undefined): string =>
  type === "comment" ? "留言" : CONTENT_TYPE_LABELS[contentType ?? ""] ?? "動態";

/**
 * Hours a case has waited (since it became open). null when the time is unknown.
 * @param {number|null} openedAt epoch ms
 * @param {number} now epoch ms
 * @return {number|null} whole hours
 */
export const waitedHours = (openedAt: number | null | undefined, now: number): number | null =>
  typeof openedAt === "number" ? Math.max(0, Math.floor((now - openedAt) / 3600000)) : null;
export const isOverdue = (openedAt: number | null | undefined, now: number): boolean =>
  typeof openedAt === "number" && now - openedAt > SLA_MS;
/**
 * "3 小時" / "2 天 4 小時" label for the queue.
 * @param {number|null} hours whole hours
 * @return {string} label
 */
export const waitedLabel = (hours: number | null): string => {
  if (hours === null) return "—";
  if (hours < 1) return "不到 1 小時";
  if (hours < 24) return `${hours} 小時`;
  return `${Math.floor(hours / 24)} 天 ${hours % 24} 小時`;
};
/** Resolve requires a note (1–500 chars after trimming). */
export const noteValid = (note: string | undefined): boolean => {
  const t = (note ?? "").trim();
  return t.length > 0 && t.length <= RESOLVE_NOTE_MAX;
};
/** Decisions that change or remove something need an explicit confirmation. */
export const needsConfirm = (d: ArtistModerationDecision): boolean => d !== "dismiss";
/** "spam 2 · harassment 1" style summary, most frequent first. */
export const reasonSummary = (reasons: Record<string, number> | null | undefined): string =>
  Object.entries(reasons ?? {})
    .filter(([, n]) => typeof n === "number" && n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([r, n]) => `${reasonLabel(r)} ${n}`)
    .join("・");

const ERROR_MESSAGES: Record<string, string> = {
  UNAUTHENTICATED: "請重新登入後操作",
  FORBIDDEN: "只有營運工作人員能處理檢舉",
  NOT_FOUND: "找不到這筆檢舉（可能已被刪除）",
  TEMPORARILY_UNAVAILABLE: "服務暫時無法使用，請稍後再試",
};
const INVALID_REASONS: Record<string, string> = {
  note: "請填寫處理備註（1–500 字）",
  decision: "這筆檢舉不能使用這個處置",
  author: "找不到內容作者，無法執行此處置",
  status: "狀態篩選不正確",
  type: "類型篩選不正確",
};
/**
 * Backend {error, reason} → message.
 * @param {string|undefined} code error
 * @param {string|undefined} reason reason
 * @param {number} status HTTP status
 * @return {string} message
 */
export const moderationErrorMessage = (code: string | undefined, reason: string | undefined, status: number): string => {
  if (code === "INVALID_ARGUMENT") return INVALID_REASONS[reason ?? ""] ?? "輸入內容不正確";
  return ERROR_MESSAGES[code ?? ""] ?? (status === 404 ? "後端尚未部署此功能（artistHub）" : "操作未完成，請重試");
};
